import React, { useState, useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Box,
    Typography,
    Chip,
    Autocomplete,
    InputAdornment,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    AccountBalance as ContractIcon,
    Public as PublicIcon,
    Edit as ManualIcon,
    ContentCopy as CopyIcon,
    CheckCircle as ValidIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { ethers } from 'ethers';

interface AddressOption {
    name: string;
    address: string;
    type: 'memer' | 'protocol' | 'manual';
    description?: string;
}

interface AddressSelectorProps {
    value: string;
    onChange: (address: string) => void;
    contracts: Array<{ name: string; address: string; abi?: any }>;
    protocolAddresses?: Array<{ name: string; address: string; description?: string }>;
    label?: string;
    placeholder?: string;
    allowManualInput?: boolean;
    required?: boolean;
    disabled?: boolean;
    helperText?: string;
    error?: boolean;
}

const AddressSelector: React.FC<AddressSelectorProps> = ({
    value,
    onChange,
    contracts,
    protocolAddresses = [],
    label = "Address",
    placeholder = "Select contract or enter address",
    allowManualInput = true,
    required = false,
    disabled = false,
    helperText,
    error = false
}) => {
    const [isManualMode, setIsManualMode] = useState(false);
    const [isValidAddress, setIsValidAddress] = useState(true);

    // Create address options from contracts, protocol addresses, and manual input
    const addressOptions: AddressOption[] = [
        ...contracts.map(contract => ({
            name: contract.name,
            address: contract.address,
            type: 'memer' as const,
            description: `Memer ecosystem contract`
        })),
        ...(protocolAddresses || []).map(protocol => ({
            name: protocol.name,
            address: protocol.address,
            type: 'protocol' as const,
            description: protocol.description || 'External protocol contract'
        })),
        ...(allowManualInput ? [{
            name: 'Manual Input',
            address: 'manual',
            type: 'manual' as const,
            description: 'Enter custom address'
        }] : [])
    ];

    // Validate address format
    useEffect(() => {
        if (value && value !== 'manual') {
            try {
                const isValid = ethers.utils.isAddress(value);
                setIsValidAddress(isValid);
            } catch {
                setIsValidAddress(false);
            }
        } else {
            setIsValidAddress(true);
        }
    }, [value]);

    // Check if current value matches a known contract
    const currentOption = addressOptions.find(option => option.address === value);
    const isKnownContract = currentOption && currentOption.type === 'memer';

    // Handle selection change - with freeSolo, newValue can be a string or AddressOption
    const handleSelectionChange = (newValue: string | AddressOption | null) => {
        if (!newValue) {
            onChange('');
            setIsManualMode(false);
            return;
        }

        // Handle string input (from freeSolo typing)
        if (typeof newValue === 'string') {
            // User typed/pasted a string directly
            onChange(newValue);
            setIsManualMode(false);
            return;
        }

        // Handle AddressOption selection
        if (newValue.type === 'manual') {
            setIsManualMode(true);
            onChange('');
        } else {
            setIsManualMode(false);
            onChange(newValue.address);
        }
    };

    // Handle manual input change
    const handleManualInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
    };

    // Copy address to clipboard
    const handleCopyAddress = () => {
        if (value && value !== 'manual') {
            navigator.clipboard.writeText(value);
        }
    };

    // Format address for display
    const formatAddress = (address: string) => {
        if (address.length <= 10) return address;
        return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    };

    // Render manual input mode
    if (isManualMode || (value && !currentOption && value !== '')) {
        return (
            <FormControl fullWidth error={error || !isValidAddress}>
                <TextField
                    label={label}
                    value={value}
                    onChange={handleManualInputChange}
                    placeholder="0x..."
                    disabled={disabled}
                    required={required}
                    error={error || !isValidAddress}
                    helperText={
                        !isValidAddress ? 'Invalid Ethereum address format' :
                            helperText || 'Enter a valid Ethereum address'
                    }
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <ManualIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: value && isValidAddress ? (
                            <InputAdornment position="end">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title="Valid address">
                                        <ValidIcon color="success" fontSize="small" />
                                    </Tooltip>
                                    <Tooltip title="Copy address">
                                        <IconButton size="small" onClick={handleCopyAddress}>
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Switch to contract selector">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setIsManualMode(false);
                                                onChange('');
                                            }}
                                        >
                                            <ContractIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </InputAdornment>
                        ) : !isValidAddress && value ? (
                            <InputAdornment position="end">
                                <Tooltip title="Invalid address format">
                                    <ErrorIcon color="error" fontSize="small" />
                                </Tooltip>
                            </InputAdornment>
                        ) : null
                    }}
                />
            </FormControl>
        );
    }

    // Render contract selector mode
    return (
        <FormControl fullWidth error={error}>
            <Autocomplete
                freeSolo
                value={currentOption || null}
                onChange={(_, newValue) => handleSelectionChange(newValue)}
                onInputChange={(_, newInputValue, reason) => {
                    // Handle manual address input (paste or type)
                    if (reason === 'input' && newInputValue) {
                        // Check if it's a valid Ethereum address
                        try {
                            if (ethers.utils.isAddress(newInputValue)) {
                                // Valid address pasted/typed - use it directly
                                onChange(newInputValue);
                                setIsManualMode(false); // Stay in autocomplete mode but with custom value
                            }
                        } catch {
                            // Not a valid address yet, let user continue typing
                        }
                    } else if (reason === 'clear') {
                        // User cleared the field
                        onChange('');
                        setIsManualMode(false);
                    }
                }}
                options={addressOptions}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                        // Handle freeSolo string input
                        return option;
                    }
                    return option.name;
                }}
                disabled={disabled}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder}
                        required={required}
                        error={error}
                        helperText={helperText}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <ContractIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                        <Box component="li" key={key} {...otherProps}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {option.type === 'memer' ? (
                                            <ContractIcon fontSize="small" color="primary" />
                                        ) : option.type === 'protocol' ? (
                                            <PublicIcon fontSize="small" color="secondary" />
                                        ) : (
                                            <ManualIcon fontSize="small" color="action" />
                                        )}
                                        <Typography variant="body1">
                                            {option.name}
                                        </Typography>
                                        {option.type === 'memer' && (
                                            <Chip
                                                label="Memer"
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {option.type === 'protocol' && (
                                            <Chip
                                                label="Protocol"
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                    {option.address !== 'manual' && (
                                        <Typography variant="caption" color="text.secondary">
                                            {formatAddress(option.address)}
                                        </Typography>
                                    )}
                                    {option.description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {option.description}
                                        </Typography>
                                    )}
                                </Box>
                                {option.address !== 'manual' && (
                                    <Tooltip title="Copy address">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(option.address);
                                            }}
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    );
                }}
            />

            {/* Show selected address details */}
            {value && isKnownContract && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Selected" size="small" color="primary" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {value}
                        </Typography>
                        <Tooltip title="Copy full address">
                            <IconButton size="small" onClick={handleCopyAddress}>
                                <CopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </FormControl>
    );
};

export default AddressSelector;
